import { useEffect, useMemo, useState } from 'react';
import type { AccountIntelRegistry, Client } from '../../types/crm';
import { normalizeIntelProfileRegistry } from '../../lib/acraRegistry';
import { fetchAccountIntel, loadCachedAccountIntel } from '../../lib/accountIntel';
import { isUenMarkedNa } from '../../lib/uenValue';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import { googleNewsSearchUrl } from '../../utils/accountActivity';

interface AccountIntelPanelProps {
  client: Client;
  canRefreshIntel: boolean;
}

function dash(value?: string | null): string {
  const t = (value ?? '').trim();
  return t || '—';
}

function formatPaidUp(registry: AccountIntelRegistry | null): string {
  if (!registry?.paidUpCapital?.trim()) return '—';
  const cur = registry.currency?.trim();
  return cur ? `${cur} ${registry.paidUpCapital.trim()}` : registry.paidUpCapital.trim();
}

export function AccountIntelPanel({ client, canRefreshIntel }: AccountIntelPanelProps) {
  const [newsHtml, setNewsHtml] = useState<string | null>(null);
  const [registry, setRegistry] = useState<AccountIntelRegistry | null>(null);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const [intelCachedAt, setIntelCachedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceLabel, setSourceLabel] = useState<string | null>(null);

  const newsUrl = useMemo(() => googleNewsSearchUrl(client.company_name), [client.company_name]);
  const hasRegistryFacts = Boolean(
    registry &&
      !registry.error &&
      (registry.uen ||
        registry.legalName ||
        registry.entityType ||
        registry.status ||
        registry.registrationDate ||
        registry.registeredAddress),
  );

  useEffect(() => {
    let cancelled = false;
    setNewsHtml(null);
    setRegistry(normalizeIntelProfileRegistry(client.intel_profile_json));
    setRegistryError(
      typeof client.intel_profile_json?.error === 'string' ? client.intel_profile_json.error : null,
    );
    void loadCachedAccountIntel(client.id).then((cached) => {
      if (cancelled || !cached) return;
      setNewsHtml(cached.newsHtml || null);
      setRegistry(cached.registry);
      setRegistryError(cached.registryError);
      setIntelCachedAt(cached.cachedAt ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [client.id, client.intel_profile_json]);

  async function handleRefresh() {
    setLoading(true);
    setError(null);
    setSourceLabel(null);
    try {
      const result = await fetchAccountIntel(client);
      setNewsHtml(result.newsHtml || null);
      setRegistry(result.registry);
      setRegistryError(result.registryError);
      setIntelCachedAt(result.cachedAt ?? new Date().toISOString());
      setSourceLabel(formatIntelSource(result.source));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load intelligence.');
    } finally {
      setLoading(false);
    }
  }

  const hasContent = hasRegistryFacts || Boolean(newsHtml) || Boolean(registryError);

  return (
    <section className="account-side-card account-intel-card">
      <div className="account-side-card__head">
        <h2 className="account-side-card__title">Market intelligence</h2>
        {canRefreshIntel && (
          <button
            type="button"
            className="account-side-btn"
            disabled={loading}
            onClick={() => void handleRefresh()}
          >
            {loading ? 'Searching…' : hasContent ? 'Refresh' : 'Generate'}
          </button>
        )}
      </div>

      <p className="account-side-muted account-intel-intro">
        Registry facts from <strong>free ACRA open data</strong> (Bizfile-equivalent: UEN, status,
        type, address). Recent news from public web search
        {client.website?.trim() ? ' + website' : ''}.
      </p>
      {!client.uen?.trim() ? (
        <p className="account-side-muted account-intel-hint">
          Tip: add the company <strong>UEN</strong> in Edit profile for the most accurate match, or set{' '}
          <strong>NA</strong> if none.
        </p>
      ) : isUenMarkedNa(client.uen) ? (
        <p className="account-side-muted account-intel-hint">
          UEN marked <strong>NA</strong> (no Singapore entity / not applicable).
        </p>
      ) : null}
      {sourceLabel && <p className="account-side-muted">{sourceLabel}</p>}
      {intelCachedAt && (
        <p className="account-side-muted">Updated {formatSingaporeDateTime(intelCachedAt)}</p>
      )}
      {error && <p className="account-intel-error">{error}</p>}
      {loading && !hasContent && (
        <div className="account-intel-loading">
          <div className="empty-state__spinner" />
        </div>
      )}

      <div className="account-intel-section">
        <h3 className="account-intel-section__title">Company registry</h3>
        {registryError && !hasRegistryFacts ? (
          <p className="account-intel-hint">{registryError}</p>
        ) : hasRegistryFacts && registry ? (
          <dl className="account-intel-facts">
            <div>
              <dt>UEN</dt>
              <dd className="mono">{dash(registry.uen)}</dd>
            </div>
            <div>
              <dt>Legal name</dt>
              <dd>{dash(registry.legalName)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{dash(registry.status)}</dd>
            </div>
            <div>
              <dt>Entity type</dt>
              <dd>{dash(registry.entityType)}</dd>
            </div>
            <div>
              <dt>Registration date</dt>
              <dd>{dash(registry.registrationDate)}</dd>
            </div>
            <div>
              <dt>Registered address</dt>
              <dd>{dash(registry.registeredAddress)}</dd>
            </div>
            {registry.primarySsic ? (
              <div>
                <dt>Primary SSIC</dt>
                <dd>{dash(registry.primarySsic)}</dd>
              </div>
            ) : null}
            {registry.paidUpCapital ? (
              <div>
                <dt>Paid-up capital</dt>
                <dd>{formatPaidUp(registry)}</dd>
              </div>
            ) : null}
          </dl>
        ) : (
          <p className="account-intel-hint">
            No registry profile yet. Click Generate (UEN optional but recommended).
          </p>
        )}
        {hasRegistryFacts && registry?.source ? (
          <p className="account-side-muted account-intel-meta">
            Source: {registry.source}
            {registry.fetchedAt ? ` · ${formatSingaporeDateTime(registry.fetchedAt)}` : ''}
          </p>
        ) : null}
      </div>

      <div className="account-intel-section">
        <h3 className="account-intel-section__title">Recent news</h3>
        {newsHtml ? (
          <div className="account-intel-html" dangerouslySetInnerHTML={{ __html: newsHtml }} />
        ) : (
          <p className="account-intel-hint">No news brief yet. Click Generate to search the web.</p>
        )}
        <a href={newsUrl} target="_blank" rel="noreferrer" className="account-side-link account-intel-news">
          Google News →
        </a>
      </div>
    </section>
  );
}

function formatIntelSource(source?: string): string | null {
  if (!source) return null;
  if (source === 'gemini-google-search') return 'News source: Gemini + Google Search.';
  if (source === 'gemini-summary') return 'News source: Gemini summary from web results.';
  if (source.includes('brave')) return 'News source: Official website + Brave Search + web.';
  if (source.includes('website')) return 'News source: Official website + web search.';
  if (source === 'edge' || source === 'cache') return null;
  return null;
}
